import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Check, X, Flag, Trash2, Clock, Filter, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: Check },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: X },
  flagged: { label: 'Flagged', color: 'bg-orange-100 text-orange-800', icon: Flag },
};

const StarDisplay = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} className={`h-3.5 w-3.5 ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-stone-200'}`} />
    ))}
  </div>
);

const AdminReviews = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const token = localStorage.getItem('token');

  const fetchReviews = async (retries = 2) => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/api/admin/reviews${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(res.data.reviews || []);
    } catch (err) {
      if (retries > 0 && (!err.response || err.response.status >= 500)) {
        await new Promise(r => setTimeout(r, 1500));
        return fetchReviews(retries - 1);
      }
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, [statusFilter]);

  const updateStatus = async (reviewId, newStatus) => {
    try {
      await axios.put(`${API}/api/admin/reviews/${reviewId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Review ${newStatus}`);
      fetchReviews();
    } catch { toast.error('Failed to update review'); }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Permanently delete this review?')) return;
    try {
      await axios.delete(`${API}/api/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      fetchReviews();
    } catch { toast.error('Failed to delete review'); }
  };

  const counts = {
    all: reviews.length,
    pending: reviews.filter(r => r.status === 'pending').length,
    approved: reviews.filter(r => r.status === 'approved').length,
    flagged: reviews.filter(r => r.status === 'flagged').length,
    rejected: reviews.filter(r => r.status === 'rejected').length,
  };

  return (
    <div className="max-w-7xl" data-testid="admin-reviews-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <Star className="h-6 w-6 text-amber-600" />
          Review Moderation
        </h1>
        <p className="text-sm text-stone-500 mt-1">Manage public reviews left by honey buyers on the Find Honey page.</p>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="review-status-filters">
        {[
          { key: '', label: 'All', count: counts.all },
          { key: 'pending', label: 'Pending', count: counts.pending },
          { key: 'approved', label: 'Approved', count: counts.approved },
          { key: 'flagged', label: 'Flagged', count: counts.flagged },
          { key: 'rejected', label: 'Rejected', count: counts.rejected },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              statusFilter === tab.key
                ? 'bg-amber-600 text-white shadow-sm'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid={`filter-${tab.key || 'all'}`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-1.5 text-xs ${statusFilter === tab.key ? 'text-amber-200' : 'text-stone-400'}`}>
                ({tab.count})
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-stone-400">Loading reviews...</div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
          <Star className="h-12 w-12 text-stone-200 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-stone-700">No reviews found</h3>
          <p className="text-sm text-stone-500 mt-1">
            {statusFilter ? `No ${statusFilter} reviews.` : 'Reviews will appear here when buyers leave them on the Find Honey page.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map(review => {
            const cfg = STATUS_CONFIG[review.status] || STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            return (
              <div key={review.id} className="bg-white rounded-xl border border-stone-200 p-4 hover:border-stone-300 transition-colors" data-testid={`review-${review.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-semibold text-stone-900">{review.reviewer_name}</span>
                      <StarDisplay rating={review.rating} />
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                        <StatusIcon className="h-3 w-3" /> {cfg.label}
                      </span>
                    </div>
                    {review.comment && (
                      <p className="text-sm text-stone-600 mt-1">{review.comment}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-stone-400">
                      <span>For: <strong className="text-stone-600">{review.beekeeper_name}</strong></span>
                      <span>{new Date(review.created_at).toLocaleDateString()}</span>
                      {review.moderated_by && (
                        <span>Moderated by {review.moderated_by}</span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {review.status !== 'approved' && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => updateStatus(review.id, 'approved')}
                        className="h-8 w-8 p-0 text-green-600 hover:bg-green-50 hover:text-green-700"
                        title="Approve"
                        data-testid={`approve-${review.id}`}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    {review.status !== 'rejected' && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => updateStatus(review.id, 'rejected')}
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                        title="Reject"
                        data-testid={`reject-${review.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    {review.status !== 'flagged' && (
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => updateStatus(review.id, 'flagged')}
                        className="h-8 w-8 p-0 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                        title="Flag"
                        data-testid={`flag-${review.id}`}
                      >
                        <Flag className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => deleteReview(review.id)}
                      className="h-8 w-8 p-0 text-stone-400 hover:bg-red-50 hover:text-red-600"
                      title="Delete"
                      data-testid={`delete-${review.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
